/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Button,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { UiHelper } from '../../../common/helpers/UiHelper'
import { AppConfig } from '../../../AppConfig'
import { AppHelper } from '../../../helpers/AppHelper'
import { isMainOrgTeam } from './TeamsHelper'

export function TeamStaffDrawer(props) {
  const {
    open,
    onClose,
    team,
    parentOrgId,
  } = props

  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [error, setError] = useState(null)

  const teamId = team ? (team.id || team.orgId) : null

  useEffect(() => {
    if (open && team) {
      fetchStaff()
    } else {
      setUsers([])
      setError(null)
    }
  }, [open, teamId])

  const fetchStaff = async () => {
    if (!team) return
    setLoading(true)
    setError(null)

    try {
      const pOrgId = parentOrgId || (team && team.parentOrgId) || (team && (team.id || team.orgId))
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['miscs', 'get-org-users2'],
        queryParams: {
          orgId: pOrgId,
          parentOrgId: pOrgId,
        },
      })

      if (response && response.err) {
        throw response.err
      }

      const rawItems = Array.isArray(response && response.items)
        ? response.items
        : (response && response.items && typeof response.items === 'object')
        ? Helper.dictToArray(response.items)
        : []

      const isMain = isMainOrgTeam(team, pOrgId)
      const targetTeamId = team ? (team.id || team.orgId) : null
      const parsedTeam = targetTeamId ? AppHelper.parseOrgUnitId(targetTeamId) : null
      const subId = (parsedTeam && parsedTeam.subOrgId) || (team && team.parentOrgId ? team.id : null)

      const userMap = new Map()

      for (const item of rawItems) {
        if (!item) continue
        const itemUserId = item.userId || item.id || item.email
        if (!itemUserId) continue

        let matches = false
        const matchedRoles = []

        if (item.accessLevels && typeof item.accessLevels === 'object') {
          for (const [k, v] of Object.entries(item.accessLevels)) {
            const parsedK = AppHelper.parseOrgUnitId(k)
            if (isMain) {
              if (k === pOrgId || (parsedK && parsedK.orgId === pOrgId && !parsedK.subOrgId)) {
                matches = true
                if (v && Array.isArray(v.roles)) matchedRoles.push(...v.roles)
              }
            } else {
              if (
                k === targetTeamId ||
                (parsedK && parsedK.subOrgId && (parsedK.subOrgId === targetTeamId || (subId && parsedK.subOrgId === subId))) ||
                (subId && k === subId)
              ) {
                matches = true
                if (v && Array.isArray(v.roles)) matchedRoles.push(...v.roles)
              }
            }
          }
        }

        if (item.orgId) {
          const parsedItemOrg = AppHelper.parseOrgUnitId(item.orgId)
          if (isMain) {
            if (item.orgId === pOrgId || (parsedItemOrg && parsedItemOrg.orgId === pOrgId && !parsedItemOrg.subOrgId)) {
              matches = true
              if (Array.isArray(item.roles)) matchedRoles.push(...item.roles)
            }
          } else {
            if (
              item.orgId === targetTeamId ||
              (parsedItemOrg && parsedItemOrg.subOrgId && (parsedItemOrg.subOrgId === targetTeamId || (subId && parsedItemOrg.subOrgId === subId))) ||
              (subId && item.orgId === subId)
            ) {
              matches = true
              if (Array.isArray(item.roles)) matchedRoles.push(...item.roles)
            }
          }
        }

        if (matches) {
          const directRoles = Array.isArray(item.roles) ? item.roles : (typeof item.roles === 'string' && item.roles ? [item.roles] : [])
          const allRoles = Array.from(new Set([...directRoles, ...matchedRoles]))
          if (!userMap.has(itemUserId)) {
            userMap.set(itemUserId, {
              ...item,
              id: itemUserId,
              roles: allRoles,
            })
          } else {
            const existing = userMap.get(itemUserId)
            const combinedRoles = Array.from(new Set([...(existing.roles || []), ...allRoles]))
            userMap.set(itemUserId, {
              ...existing,
              roles: combinedRoles,
            })
          }
        }
      }

      const sorted = Array.from(userMap.values()).sort((a, b) =>
        (Helper.formatName(a) || a.email || '').localeCompare(Helper.formatName(b) || b.email || '')
      )
      setUsers(sorted)
      setLoading(false)
    } catch (err) {
      console.error('Failed to fetch team staff:', err)
      setError(Helper.getErrorMsg(err) || 'Failed to load staff members.')
      setLoading(false)
    }
  }

  const staffPageUrl = `${UiHelper.baseRoute(parentOrgId)}/staff`

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { width: Math.min(600, window.innerWidth - 32), maxWidth: '100%' },
      }}
    >
      <Box p={3} display='flex' flexDirection='column' style={{ height: '100%' }}>
        {/* Header */}
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
          <Box display='flex' alignItems='center'>
            <PeopleOutlineIcon color='primary' style={{ marginRight: 8, fontSize: 28 }} />
            <div>
              <Typography variant='h6' style={{ fontWeight: 600, lineHeight: 1.2 }}>
                Team Staff Members
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {team ? team.title : ''}
              </Typography>
            </div>
          </Box>
          <IconButton size='small' onClick={onClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider style={{ margin: '12px 0 16px 0' }} />

        {/* Content */}
        <Box flexGrow={1} style={{ overflowY: 'auto' }}>
          {error && (
            <Box mb={2}>
              <Alert
                severity='error'
                action={
                  <Button color='inherit' size='small' onClick={fetchStaff}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          )}

          {loading && (
            <Box display='flex' justifyContent='center' alignItems='center' p={6}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && !error && (
            <>
              {users.length === 0 ? (
                <Box textAlign='center' py={6}>
                  <PeopleOutlineIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: 12 }} />
                  <Typography variant='subtitle1' color='textSecondary' gutterBottom>
                    No staff members assigned to this team yet.
                  </Typography>
                  <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
                    You can add and manage members from the full staff management page.
                  </Typography>
                  <Button
                    variant='outlined'
                    color='primary'
                    size='small'
                    component={Link}
                    to={staffPageUrl}
                    startIcon={<OpenInNewIcon />}
                    onClick={onClose}
                  >
                    Manage Members in Full Page
                  </Button>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}>
                  <Table size='small'>
                    <TableHead style={{ backgroundColor: '#f9fafb' }}>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>Email / Phone</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>Roles</TableCell>
                        <TableCell style={{ fontWeight: 600 }} align='center'>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user, idx) => {
                        const name = Helper.formatName(user) || '-'
                        const contact = user.email || user.phone || '-'
                        const roles = Array.isArray(user.roles) ? user.roles.join(', ') : (user.roles || '-')
                        const isLive = user.state === 'LIVE' || !user.state
                        return (
                          <TableRow key={user.id || user.userId || idx} hover>
                            <TableCell style={{ fontWeight: 500 }}>{name}</TableCell>
                            <TableCell>{contact}</TableCell>
                            <TableCell>
                              <Typography variant='body2' style={{ fontSize: 12 }}>
                                {roles}
                              </Typography>
                            </TableCell>
                            <TableCell align='center'>
                              <Chip
                                size='small'
                                label={user.state || 'LIVE'}
                                style={{
                                  fontSize: 10,
                                  height: 20,
                                  backgroundColor: isLive ? '#e8f5e9' : '#eeeeee',
                                  color: isLive ? '#2e7d32' : '#757575',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>

        {/* Footer Actions */}
        <Divider style={{ margin: '16px 0 12px 0' }} />
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='caption' color='textSecondary'>
            {users.length} {users.length === 1 ? 'member' : 'members'} found
          </Typography>
          <Box display='flex' alignItems='center'>
            <Button
              variant='contained'
              color='primary'
              size='small'
              component={Link}
              to={staffPageUrl}
              target='_blank'
              rel='noopener noreferrer'
              endIcon={<OpenInNewIcon />}
              onClick={onClose}
            >
              Manage Staff
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}
